import { createGetRequest } from 'app/selectors';
import { TooltipWrapper } from 'components/TooltipWrapper/TooltipWrapper';
import type { BimChapterEnum, WorkOrderChangeObjectData } from 'gql/graphql';
import { ActKc2StatusEnum, ProjectStatusEnum, WorkOrderStatusEnum } from 'gql/graphql';
import { useProjectData } from 'hooks/useProjectData';
import { useSendGpnAnalyticsEvent } from 'hooks/useSendGpnAnalyticsEvent';
import type { IListItem } from 'types/Contract';
import { Button, cnMixSpace } from 'uikit';

import { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { BimAddOtherTable } from './BimAddOtherTable';

interface IAddOrderOther {
    constructionTypeId?: number;
    isDisabled: boolean;
    bimOtherChapter: BimChapterEnum | undefined;
    workOrderRootObject: IListItem<number> | undefined;
    workOrderChangeObjectsData: WorkOrderChangeObjectData[];
    isFrameContract: boolean;
    onSuccess: () => void;
    workOrderStatus: WorkOrderStatusEnum | undefined;
    hasOtherActsKc2: boolean;
    withBtn?: boolean;
    isBimPresenceActs?: boolean;
    togglePopover: () => void;
    statusProject?: ProjectStatusEnum;
}

export const BimAddOrderOther = (props: IAddOrderOther) => {
    const {
        isDisabled,
        constructionTypeId,
        bimOtherChapter,
        workOrderChangeObjectsData,
        workOrderRootObject,
        withBtn = true,
        isFrameContract,
        onSuccess,
        workOrderStatus,
        hasOtherActsKc2,
        isBimPresenceActs,
        togglePopover,
        statusProject,
    } = props;
    const { id: projectId, orderId } = useParams();
    const { projectData } = useProjectData(projectId, false);

    const { sendGpnAnalyticsEvent } = useSendGpnAnalyticsEvent();

    const requestSelector = useMemo(() => createGetRequest('bimActsKC2Other'), []);
    const { data: bimActsKC2Other } = useSelector(requestSelector);

    const listItemsActsKC2Other = bimActsKC2Other?.bimActsKC2Other.items;

    const isActsValid = useMemo(() => {
        if (!listItemsActsKC2Other?.length) {
            return true;
        }

        const statusTruest = [ActKc2StatusEnum.InProgress, ActKc2StatusEnum.OnCheck];

        return listItemsActsKC2Other?.some((el: { status: ActKc2StatusEnum }) => {
            const { status } = el;

            return statusTruest.includes(status);
        });
    }, [listItemsActsKC2Other]);

    const [isOpen, setIsOpen] = useState(false);

    const isWorOrderStatusDelayed = workOrderStatus === WorkOrderStatusEnum.Delayed;
    const isWorOrderStatusCompleted = workOrderStatus === WorkOrderStatusEnum.Completed;

    const isProjectStatusDelayed = statusProject === ProjectStatusEnum.Delayed;

    const toolTipMessage = useMemo(() => {
        if (isWorOrderStatusDelayed || isWorOrderStatusCompleted) {
            return '';
        }

        if (!bimOtherChapter || !workOrderRootObject) {
            return 'Выберите раздел "Прочие" и объект';
        }

        if (
            (workOrderStatus === WorkOrderStatusEnum.InProgress && !hasOtherActsKc2) ||
            (workOrderStatus === WorkOrderStatusEnum.InProgress && !isBimPresenceActs)
        ) {
            return `В ${
                isFrameContract ? 'НЗ' : 'Расчете по Договору'
            } в статусе "В работе" добавление прочих доступно через создание Акта КС-2`;
        }

        return '';
    }, [
        isWorOrderStatusDelayed,
        isWorOrderStatusCompleted,
        bimOtherChapter,
        workOrderRootObject,
        workOrderStatus,
        hasOtherActsKc2,
        isBimPresenceActs,
        isFrameContract,
    ]);

    const isDisabledButton =
        isDisabled ||
        isWorOrderStatusDelayed ||
        isWorOrderStatusCompleted ||
        !!toolTipMessage ||
        isProjectStatusDelayed ||
        !isActsValid;

    const analyticsData = useMemo(
        () => ({
            projectId,
            orderId: Number(orderId),
            orderStatus: workOrderStatus,
            projectName: projectData?.name,
            projectType: projectData?.contractType,
            projectDO: projectData?.subsidiary?.name,
            methodology: projectData?.methodology,
        }),
        [
            orderId,
            projectData?.contractType,
            projectData?.methodology,
            projectData?.name,
            projectData?.subsidiary?.name,
            projectId,
            workOrderStatus,
        ],
    );

    const handleOnButtonClick = useCallback(() => {
        if (!hasOtherActsKc2) {
            sendGpnAnalyticsEvent({
                componentId: 'rim_NZ_add other button_pressed',
                ...analyticsData,
            });
        } else {
            sendGpnAnalyticsEvent({
                componentId: 'rim_act KC_add other button_pressed',
                ...analyticsData,
            });
        }

        setIsOpen(true);
    }, [analyticsData, hasOtherActsKc2, sendGpnAnalyticsEvent]);

    const handleOnEditCancel = useCallback(() => {
        setIsOpen(false);
        togglePopover();
    }, [togglePopover]);

    const successHandler = useCallback(() => {
        handleOnEditCancel();
        onSuccess();
    }, [handleOnEditCancel, onSuccess]);

    return (
        <>
            {withBtn && (
                <TooltipWrapper direction="rightCenter" message={toolTipMessage} isHidden={!toolTipMessage}>
                    <Button
                        className={cnMixSpace({ mR: 'xs' })}
                        label="Прочие"
                        size="s"
                        onClick={handleOnButtonClick}
                        disabled={isDisabledButton}
                        form="brick"
                        width="full"
                        view="clear"
                    />
                </TooltipWrapper>
            )}
            {isOpen && (
                <BimAddOtherTable
                    hasOtherActsKc2={hasOtherActsKc2}
                    onCancel={handleOnEditCancel}
                    constructionTypeId={constructionTypeId}
                    workOrderChangeObjectsData={workOrderChangeObjectsData}
                    isFrameContract={isFrameContract}
                    handlerSuccess={successHandler}
                    workOrderStatus={workOrderStatus}
                    workOrderObject={workOrderRootObject}
                />
            )}
        </>
    );
};

BimAddOrderOther.displayName = 'BimAddOrderOther';
